import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin, Clock, BadgeCheck, Crown, Sparkles, Star, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import SpaBadges from "@/components/spa/SpaBadges";
import { SpaCardGrid } from "@/components/spa/SpaCard";
import BookingDialog from "@/components/spa/BookingDialog";
import JsonLd from "@/components/seo/JsonLd";
import { resolveImageUrl } from "@/components/forms/ImageUploader";
import Captcha from "@/components/forms/Captcha";
import api from "@/lib/api";

/** Hidden honeypot input — bots fill it, real users don't. Off-screen + no a11y. */
function HoneypotInput({ value, onChange, testId = "honeypot-website" }) {
  return (
    <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
      <label>
        Website (leave blank)
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={value}
          onChange={onChange}
          data-testid={testId}
        />
      </label>
    </div>
  );
}

export default function SpaDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ customer_name: "", rating: 5, review: "", website: "" });
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", email: "", message: "", website: "" });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [reviewCaptcha, setReviewCaptcha] = useState(null);
  const [leadCaptcha, setLeadCaptcha] = useState(null);
  const reviewCaptchaRef = useRef(null);
  const leadCaptchaRef = useRef(null);

  const loadSpa = useCallback(async () => {
    try {
      const r = await api.get(`/spas/${slug}`);
      setData(r.data);
      document.title = `${r.data.spa.spa_name} - ${r.data.spa.locality} - Pune Spa Guide`;
    } catch {
      setData({ error: true });
    }
  }, [slug]);

  const loadReviews = useCallback(async () => {
    try {
      const r = await api.get(`/spas/${slug}/reviews`);
      setReviews(r.data.items);
    } catch (err) {
      // Non-fatal — the page still renders without reviews. Log for diagnostics.
      console.warn(`Failed to load reviews for spa "${slug}":`, err);
    }
  }, [slug]);

  useEffect(() => {
    setData(null);
    loadSpa();
    loadReviews();
  }, [loadSpa, loadReviews]);

  const submitReview = useCallback(async (e) => {
    e.preventDefault();
    if (!reviewForm.customer_name || !reviewForm.review) return toast.error("Please fill name and review");
    if (!reviewCaptcha) return toast.error("Please complete the captcha");
    setReviewLoading(true);
    try {
      await api.post(`/spas/${slug}/reviews`, { ...reviewForm, rating: parseFloat(reviewForm.rating), hcaptcha_token: reviewCaptcha });
      toast.success("Review submitted!");
      setReviewForm({ customer_name: "", rating: 5, review: "", website: "" });
      setReviewCaptcha(null);
      reviewCaptchaRef.current?.reset();
      await loadReviews();
      await loadSpa();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
      reviewCaptchaRef.current?.reset();
      setReviewCaptcha(null);
    } finally {
      setReviewLoading(false);
    }
  }, [slug, reviewForm, reviewCaptcha, loadReviews, loadSpa]);

  const submitLead = useCallback(async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.phone || !leadForm.message) return toast.error("Name, phone & message required");
    if (!leadCaptcha) return toast.error("Please complete the captcha");
    setLeadLoading(true);
    try {
      await api.post(`/spas/${slug}/leads`, { ...leadForm, hcaptcha_token: leadCaptcha });
      toast.success("Inquiry sent! The spa will contact you shortly.");
      setLeadForm({ name: "", phone: "", email: "", message: "", website: "" });
      setLeadCaptcha(null);
      leadCaptchaRef.current?.reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send inquiry");
      leadCaptchaRef.current?.reset();
      setLeadCaptcha(null);
    } finally {
      setLeadLoading(false);
    }
  }, [slug, leadForm, leadCaptcha]);

  if (!data) return <div className="max-w-6xl mx-auto px-4 py-20 text-center text-[#4B5563]">Loading...</div>;
  if (data.error) return <div className="max-w-6xl mx-auto px-4 py-20 text-center">Spa not found</div>;

  const spa = data.spa;
  const nearby = data.nearby || [];
  const galleryRaw = (spa.gallery && spa.gallery.length ? spa.gallery : [spa.cover_image].filter(Boolean));
  const gallery = galleryRaw.map(resolveImageUrl);
  const main = gallery[0] || "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1200&q=80";

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(spa.address + ", " + spa.locality + ", " + (spa.city || "Pune"))}&output=embed`;

  // Plan-gated features:
  //   - FREE     → call only (no WhatsApp, no inquiry, no booking)
  //   - FEATURED → call + inquiry (still no WhatsApp / booking)
  //   - PREMIUM  → call + WhatsApp + inquiry + booking
  // We read backend-provided flags (which mirror PLAN_LIMITS server-side) so
  // gating logic stays in one place.
  const inquiryEnabled = spa.leads_enabled ?? (spa.plan !== "free");
  const whatsappEnabled = !!(spa.whatsapp_enabled && spa.whatsapp);
  const bookingEnabled = !!spa.booking_enabled;

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "HealthAndBeautyBusiness",
    name: spa.spa_name,
    image: gallery, // already resolved to absolute URLs
    address: { "@type": "PostalAddress", streetAddress: spa.address, addressLocality: spa.locality, addressRegion: "Maharashtra", addressCountry: "IN" },
    telephone: spa.phone,
    priceRange: spa.price_range,
    aggregateRating: { "@type": "AggregateRating", ratingValue: spa.rating, reviewCount: reviews.length || 1 },
  };

  return (
    <div data-testid="spa-detail-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <JsonLd data={ldJson} />

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/spas">Spas</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/spas-in-${spa.locality.toLowerCase().replace(/\s/g,'-')}`}>{spa.locality}</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem className="text-[#111827]">{spa.spa_name}</BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5">
        <div>
          <SpaBadges featured={spa.featured} premium={spa.premium} verified={spa.verified} />
          <h1 className="font-heading text-3xl sm:text-4xl text-[#111827] mt-2" data-testid="spa-detail-title">{spa.spa_name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-[#4B5563]">
            <span className="flex items-center gap-1"><MapPin size={14}/> {spa.address}</span>
            <span className="flex items-center gap-1"><Star size={14} className="fill-[#C9A86A] text-[#C9A86A]"/> {spa.rating?.toFixed(1)} ({reviews.length} review{reviews.length === 1 ? "" : "s"})</span>
            <span>{spa.price_range}</span>
          </div>
        </div>
      </div>

      {/* GALLERY */}
      <div data-testid="spa-gallery" className="grid grid-cols-4 gap-2 mb-8">
        <div className="col-span-4 sm:col-span-2 row-span-2">
          <AspectRatio ratio={16/11} className="rounded-2xl overflow-hidden bg-[#F5EFE6]">
            <img src={main} alt={spa.spa_name} className="w-full h-full object-cover" />
          </AspectRatio>
        </div>
        {gallery.slice(1, 5).map((g, i) => (
          <div key={`g-${g}-${i}`} className="col-span-2 sm:col-span-1">
            <AspectRatio ratio={1} className="rounded-xl overflow-hidden bg-[#F5EFE6]">
              <img src={g} alt={`${spa.spa_name} ${i+2}`} className="w-full h-full object-cover" />
            </AspectRatio>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* MAIN CONTENT */}
        <div className="lg:col-span-2 space-y-8">
          <Tabs defaultValue="overview" data-testid="spa-detail-tabs">
            <TabsList className="bg-[#F5EFE6]">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews ({reviews.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-5">
              <h3 className="font-heading text-xl mb-3">About {spa.spa_name}</h3>
              <p className="text-sm text-[#1F2937] leading-7 whitespace-pre-line">{spa.description}</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-2"><Clock size={16} className="text-[#1F4E3D] mt-0.5"/><div><div className="font-medium">Opening Hours</div><div className="text-[#4B5563]">{spa.opening_hours}</div></div></div>
                <div className="flex items-start gap-2"><MapPin size={16} className="text-[#1F4E3D] mt-0.5"/><div><div className="font-medium">Address</div><div className="text-[#4B5563]">{spa.address}</div></div></div>
                <div className="flex items-start gap-2"><BadgeCheck size={16} className="text-[#1F4E3D] mt-0.5"/><div><div className="font-medium">Category</div><div className="text-[#4B5563]">{spa.category}</div></div></div>
                <div className="flex items-start gap-2"><Sparkles size={16} className="text-[#1F4E3D] mt-0.5"/><div><div className="font-medium">Price Range</div><div className="text-[#4B5563]">{spa.price_range}</div></div></div>
              </div>
            </TabsContent>
            <TabsContent value="services" className="pt-5">
              <h3 className="font-heading text-xl mb-3">Services & Treatments</h3>
              <ul className="grid sm:grid-cols-2 gap-2">
                {(spa.services || []).map((s) => (
                  <li key={s} className="flex items-center gap-2 text-sm bg-[#F5EFE6] rounded-xl px-3 py-2">
                    <ChevronRight size={14} className="text-[#1F4E3D]"/> {s}
                  </li>
                ))}
              </ul>
              <div className="mt-6 text-xs text-[#4B5563] italic">Final pricing depends on therapist, duration & add-ons. Contact spa for exact quotes.</div>
            </TabsContent>
            <TabsContent value="reviews" className="pt-5">
              <h3 className="font-heading text-xl mb-3">Guest Reviews</h3>
              {reviews.length === 0 ? (
                <div className="text-sm text-[#4B5563] mb-4">No reviews yet. Be the first to review!</div>
              ) : (
                <div className="space-y-4 mb-6">
                  {reviews.map((r) => (
                    <div key={r.id} className="rounded-2xl border border-[#E7DDCF] bg-white p-4">
                      <div className="flex justify-between">
                        <div className="font-medium">{r.customer_name}</div>
                        <div className="flex items-center gap-1 text-sm"><Star size={14} className="fill-[#C9A86A] text-[#C9A86A]"/> {r.rating}</div>
                      </div>
                      <p className="text-sm text-[#1F2937] mt-2 leading-relaxed">{r.review}</p>
                      <div className="text-xs text-[#4B5563] mt-2">{new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              )}
              <form onSubmit={submitReview} className="rounded-2xl border border-[#E7DDCF] bg-[#F5EFE6]/40 p-5 space-y-3" data-testid="review-form">
                <h4 className="font-heading text-lg">Write a Review</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Your Name</Label>
                    <Input value={reviewForm.customer_name} onChange={(e) => setReviewForm({...reviewForm, customer_name: e.target.value})} required data-testid="review-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Rating</Label>
                    <select value={reviewForm.rating} onChange={(e) => setReviewForm({...reviewForm, rating: e.target.value})} className="w-full h-10 rounded-xl border border-[rgba(17,24,39,0.14)] px-3 bg-white text-sm" data-testid="review-rating-select">
                      {[5,4,3,2,1].map((r) => <option key={r} value={r}>{r} Star{r !== 1 && "s"}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Your Review</Label>
                  <Textarea rows={3} value={reviewForm.review} onChange={(e) => setReviewForm({...reviewForm, review: e.target.value})} required data-testid="review-text-input" />
                </div>
                <HoneypotInput
                  value={reviewForm.website}
                  onChange={(e) => setReviewForm({ ...reviewForm, website: e.target.value })}
                  testId="review-honeypot-input"
                />
                <Captcha
                  ref={reviewCaptchaRef}
                  onToken={setReviewCaptcha}
                  testId="review-captcha"
                />
                <Button type="submit" disabled={reviewLoading || !reviewCaptcha} data-testid="review-submit-button" className="bg-[#111827] hover:bg-[#0B1220] text-white">
                  {reviewLoading ? "Submitting..." : "Submit Review"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* MAP */}
          <div>
            <h3 className="font-heading text-xl mb-3">Location</h3>
            <div className="rounded-2xl overflow-hidden border border-[#E7DDCF]">
              <iframe data-testid="spa-map-iframe" src={mapSrc} title="Map" width="100%" height="320" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>

          {/* NEARBY */}
          {nearby.length > 0 && (
            <div>
              <h3 className="font-heading text-xl mb-3">Nearby Spas in {spa.locality}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {nearby.slice(0, 3).map((n) => <SpaCardGrid key={n.id} spa={n} />)}
              </div>
            </div>
          )}
        </div>

        {/* STICKY CONTACT */}
        <aside className="lg:col-span-1">
          <div data-testid="spa-sticky-contact-card" className="sticky top-20 rounded-2xl bg-white border border-[#E7DDCF] p-5 shadow-[0_12px_30px_rgba(17,24,39,0.08)]">
            <div className="text-xs uppercase tracking-wider text-[#7A5A22]">Contact</div>
            <div className="font-heading text-lg text-[#111827] mt-1">Connect Directly</div>
            <div className="mt-4 flex flex-col gap-3" data-testid="spa-contact-cta-stack">
              {(() => {
                // Build the visible CTA list dynamically based on plan flags.
                // Each entry returns a React element that already styles itself
                // green (#1F4E3D) or brown (#C9A86A) according to its position
                // in the stack — first CTA green, second brown, third green, etc.
                //
                // Inquiry is special-cased because it's a Dialog (rendered just
                // below this stack); we still reserve a position in the
                // alternating sequence so its outline button keeps the right
                // accent ring color.
                const ctas = [];
                ctas.push({ key: "call", kind: "link" });
                if (whatsappEnabled) ctas.push({ key: "whatsapp", kind: "link" });
                if (bookingEnabled) ctas.push({ key: "booking", kind: "booking" });
                if (inquiryEnabled) ctas.push({ key: "inquiry", kind: "inquiry" });

                /** Return solid-fill classes for the given alternating position. */
                const solid = (i) => i % 2 === 0
                  ? "bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white"
                  : "bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827]";

                return ctas.map((c, i) => {
                  const scheme = i % 2 === 0 ? "green" : "brown";
                  if (c.kind === "link") {
                    if (c.key === "call") {
                      return (
                        <a key="call" href={`tel:${spa.phone}`} className="block" data-testid="spa-contact-call-button">
                          <Button className={`w-full h-11 gap-2 ${solid(i)}`}>
                            <Phone size={16} /> Call {spa.phone}
                          </Button>
                        </a>
                      );
                    }
                    if (c.key === "whatsapp") {
                      return (
                        <a
                          key="whatsapp"
                          href={`https://wa.me/${spa.whatsapp.replace(/[^0-9]/g, '')}?text=Hi, I found your spa on Pune Spa Guide`}
                          target="_blank"
                          rel="noreferrer"
                          className="block"
                          data-testid="spa-contact-whatsapp-button"
                        >
                          <Button className={`w-full h-11 gap-2 ${solid(i)}`}>
                            <MessageCircle size={16} /> WhatsApp
                          </Button>
                        </a>
                      );
                    }
                  }
                  if (c.kind === "booking") {
                    return (
                      <BookingDialog
                        key="booking"
                        slug={spa.slug}
                        services={spa.services || []}
                        slotMinutes={spa.booking_slot_minutes || 60}
                        colorScheme={scheme}
                      />
                    );
                  }
                  if (c.kind === "inquiry") {
                    return (
                      <Dialog key="inquiry">
                        <DialogTrigger asChild>
                          <Button
                            className={`w-full h-11 gap-2 ${solid(i)}`}
                            data-testid="spa-inquiry-open-button"
                          >
                            Send Inquiry
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Inquiry to {spa.spa_name}</DialogTitle>
                            <DialogDescription>The spa will get back to you on your provided phone/email.</DialogDescription>
                          </DialogHeader>
                          <form onSubmit={submitLead} className="space-y-3" data-testid="inquiry-form">
                            <div><Label className="text-xs">Your Name *</Label><Input data-testid="inquiry-name-input" value={leadForm.name} onChange={(e) => setLeadForm({...leadForm, name: e.target.value})} required /></div>
                            <div className="grid grid-cols-2 gap-3">
                              <div><Label className="text-xs">Phone *</Label><Input data-testid="inquiry-phone-input" value={leadForm.phone} onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})} required /></div>
                              <div><Label className="text-xs">Email</Label><Input data-testid="inquiry-email-input" type="email" value={leadForm.email} onChange={(e) => setLeadForm({...leadForm, email: e.target.value})} /></div>
                            </div>
                            <div><Label className="text-xs">Your Message *</Label><Textarea rows={3} data-testid="inquiry-message-input" value={leadForm.message} onChange={(e) => setLeadForm({...leadForm, message: e.target.value})} required /></div>
                            <HoneypotInput
                              value={leadForm.website}
                              onChange={(e) => setLeadForm({ ...leadForm, website: e.target.value })}
                              testId="inquiry-honeypot-input"
                            />
                            <Captcha
                              ref={leadCaptchaRef}
                              onToken={setLeadCaptcha}
                              testId="inquiry-captcha"
                            />
                            <DialogFooter>
                              <Button type="submit" disabled={leadLoading || !leadCaptcha} data-testid="inquiry-submit-button" className="bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827]">
                                {leadLoading ? "Sending..." : "Send Inquiry"}
                              </Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                    );
                  }
                  return null;
                });
              })()}
              {!inquiryEnabled && (
                <div
                  className="w-full rounded-md border border-dashed border-[#E7DDCF] bg-[#FBF7F1] px-3 py-2 text-xs text-[#4B5563] text-center"
                  data-testid="spa-inquiry-disabled-note"
                >
                  Online inquiries are not available for this listing. Please call the spa directly.
                </div>
              )}
            </div>
            <div className="mt-5 pt-5 border-t border-[#E7DDCF] text-xs text-[#4B5563] space-y-1.5">
              <div className="flex items-center gap-2"><Clock size={12}/> {spa.opening_hours}</div>
              <div className="flex items-center gap-2"><MapPin size={12}/> {spa.locality}, {spa.city || "Pune"}</div>
              {spa.verified && <div className="flex items-center gap-2 text-[#1F4E3D]"><BadgeCheck size={12}/> Verified Listing</div>}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
